import type { Notification } from 'shared-types';
import type { ReduxRootState } from 'types';

interface Params {
  messages: ReduxRootState['messages'];
  isMultiUser: boolean;
  type: Notification['type'];
}

const getNotificationMessageByType = ({
  messages,
  isMultiUser,
  type
}: Params) => {
  const header = {
    'achievement-access-denied':
      messages.notificationsTextAchievementAccessDenied,
    'achievement-access-granted':
      messages.notificationsTextAchievementAccessGranted,
    'achievement-completion': messages.notificationsTextAchievementCompletion,
    'achievement-conversation-closing-approval':
      messages.notificationsTextAchievementConversationClosingApproval,
    'answer-comment': messages.notificationsTextAnswerComment,
    'answer-request-changes': messages.notificationsTextChangesRequestedAnswer,
    'answer-review': messages.notificationsTextAnswerReview,
    'calendar-event-access-added':
      messages.notificationsTextCalendarEventAccessGranted,
    'calendar-event-access-denied':
      messages.notificationsTextCalendarEventAccessDenied,
    'calendar-event-access-granted':
      messages.notificationsTextCalendarEventAccessGranted,
    'calendar-event-access-from-queue':
      messages.notificationsTextCalendarEventAccessFromQueue,
    'competency-access-denied':
      messages.notificationsTextCompetencyAccessDenied,
    'competency-access-granted':
      messages.notificationsTextCompetencyAccessGranted,
    'course-access-denied': messages.notificationsTextCourseAccessDenied,
    'course-access-granted': messages.notificationsTextCourseAccessGranted,
    'course-completion': messages.notificationsTextCourseCompletion,
    'course-deadline': messages.notificationsTextCourseDeadline,
    'course-evaluation-failed':
      messages.notificationsTextCourseEvaluationFailed,
    'course-evaluation-ready': messages.notificationsTextCourseEvaluationReady,
    'course-generation-failed': messages.notificationsTextCourseGenerationFailed,
    'course-generation-ready': messages.notificationsTextCourseGenerationReady,
    'discussion-comment': messages.notificationsTextDiscussionComment,
    feedback: messages.notificationsTextFeedback,
    'moodle-import-failed': messages.notificationsTextMoodleImportFailed,
    'moodle-import-ready': messages.notificationsTextMoodleImportReady,
    'individual-achievement-access-denied':
      messages.notificationsTextAchievementAccessDenied,
    'individual-achievement-access-granted':
      messages.notificationsTextAchievementAccessGranted,
    'new-achievement-admin': messages.notificationsTextNewAdminRoleAchievement,
    'new-achievement-owner': messages.notificationsTextNewOwnerRoleAchievement,
    'new-calendar-owner': messages.notificationsTextNewOwnerRoleCalendar,
    'new-competencies': messages.notificationsTextNewCompetencies,
    'new-course-admin': messages.notificationsTextNewAdminRoleCourse,
    'new-course-chapter': messages.notificationsTextNewCourseChapter,
    'new-course-content': messages.notificationsTextNewCourseContent,
    'new-course-owner': messages.notificationsTextNewOwnerRoleCourse,
    'new-group-admin': messages.notificationsTextNewAdminRoleGroup,
    'new-group-owner': messages.notificationsTextNewOwnerRoleGroup,
    'new-path-owner': messages.notificationsTextNewOwnerRolePath,
    'path-completion': messages.notificationsTextPathCompletion,
    'path-course-deadline': messages.notificationsTextPathDeadline,
    'path-step-deadline': messages.notificationsTextPathDeadline
  }[type];

  const infoMessage = {
    'achievement-access-denied':
      messages.notificationsTextAchievementAccessDeniedInfo,
    'achievement-access-granted':
      messages.notificationsTextAchievementAccessGrantedInfo,
    'achievement-completion':
      messages.notificationsTextAchievementCompletionInfo,
    'achievement-conversation-closing-approval':
      messages.notificationsTextAchievementConversationClosingApprovalInfo,
    'answer-comment': messages.notificationsTextAnswerCommentInfo,
    'answer-request-changes':
      messages.notificationsTextChangesRequestedAnswerInfo,
    'answer-review': messages.notificationsTextAnswerReviewInfo,
    'calendar-event-access-added':
      messages.notificationsTextCalendarEventAccessAddedInfo,
    'calendar-event-access-denied':
      messages.notificationsTextCalendarEventAccessDeniedInfo,
    'calendar-event-access-granted':
      messages.notificationsTextCalendarEventAccessGrantedInfo,
    'calendar-event-access-from-queue':
      messages.notificationsTextCalendarEventAccessFromQueueInfo,
    'competency-access-denied':
      messages.notificationsTextCompetencyAccessDeniedInfo,
    'competency-access-granted':
      messages.notificationsTextCompetencyAccessGrantedInfo,
    'course-access-denied': messages.notificationsTextCourseAccessDeniedInfo,
    'course-access-granted': messages.notificationsTextCourseAccessGrantedInfo,
    'course-completion': messages.notificationsTextCourseCompletionInfo,
    'course-deadline': messages.notificationsTextCourseDeadlineInfo,
    'course-evaluation-failed':
      messages.notificationsTextCourseEvaluationFailedInfo,
    'course-evaluation-ready':
      messages.notificationsTextCourseEvaluationReadyInfo,
    'course-generation-failed':
      messages.notificationsTextCourseGenerationFailedInfo,
    'course-generation-ready':
      messages.notificationsTextCourseGenerationReadyInfo,
    'discussion-comment': isMultiUser
      ? messages.notificationsTextDiscussionCommentMultipleInfo
      : messages.notificationsTextDiscussionCommentInfo,
    feedback: messages.notificationsTextFeedbackInfo,
    'moodle-import-failed': messages.notificationsTextMoodleImportFailedInfo,
    'moodle-import-ready': messages.notificationsTextMoodleImportReadyInfo,
    'individual-achievement-access-denied':
      messages.notificationsTextIndividualAchievementAccessDeniedInfo,
    'individual-achievement-access-granted':
      messages.notificationsTextIndividualAchievementAccessGrantedInfo,
    'new-achievement-admin':
      messages.notificationsTextNewAdminRoleAchievementInfo,
    'new-achievement-owner':
      messages.notificationsTextNewOwnerRoleAchievementInfo,
    'new-calendar-owner': messages.notificationsTextNewOwnerRoleCalendarInfo,
    'new-competencies': messages.notificationsTextNewCompetenciesInfo,
    'new-course-admin': messages.notificationsTextNewAdminRoleCourseInfo,
    'new-course-chapter': messages.notificationsTextNewCourseChapterInfo,
    'new-course-content': messages.notificationsTextNewCourseContentInfo,
    'new-course-owner': messages.notificationsTextNewOwnerRoleCourseInfo,
    'new-group-admin': messages.notificationsTextNewAdminRoleGroupInfo,
    'new-group-owner': messages.notificationsTextNewOwnerRoleGroupInfo,
    'new-path-owner': messages.notificationsTextNewOwnerRolePathInfo,
    'path-completion': messages.notificationsTextPathCompletionInfo,
    'path-course-deadline': messages.notificationsTextPathDeadlineInfo,
    'path-step-deadline': messages.notificationsTextPathStepDeadlineInfo
  }[type];

  return [header, infoMessage];
};

export default getNotificationMessageByType;
